/**
 * Finding Detail Page
 * 
 * WHY: After reviewing findings on the dashboard, analysts need to dive into
 * the full details of a specific finding. This page shows complete metadata,
 * linked observations, VEX status, and provides export functionality.
 * 
 * @see .docs/architecture/phase5_security_assessment.md - Phase 6 Requirements
 */

import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ArrowLeft,
  Shield,
  FileCode,
  AlertTriangle,
  Info,
  Clock,
  User,
  ExternalLink,
  Download,
  CheckCircle,
  Crosshair,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Get severity badge variant and styling.
 */
function getSeverityDisplay(label: string) {
  const lower = label.toLowerCase()
  if (lower === 'critical') {
    return { variant: 'critical' as const, color: 'text-red-500', bgColor: 'bg-red-500/10' }
  }
  if (lower === 'high') {
    return { variant: 'high' as const, color: 'text-orange-500', bgColor: 'bg-orange-500/10' }
  }
  if (lower === 'medium') {
    return { variant: 'medium' as const, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10' }
  }
  return { variant: 'low' as const, color: 'text-blue-500', bgColor: 'bg-blue-500/10' }
}

/**
 * Metadata item display component.
 */
function MetadataItem({
  label,
  value,
  icon: Icon,
  mono = false,
}: {
  label: string
  value: React.ReactNode
  icon?: React.ElementType
  mono?: boolean
}) {
  if (!value) return null

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      <span className={cn('flex items-center gap-2', mono && 'font-mono text-sm')}>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        {value}
      </span>
    </div>
  )
}

export function FindingDetailPage() {
  const { missionId, findingId } = useParams<{ missionId: string; findingId: string }>()

  // Fetch finding details
  const {
    data: finding,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['softwareiq', 'findings', missionId, findingId],
    queryFn: () => api.softwareiq.findings.get(missionId!, findingId!),
    enabled: !!missionId && !!findingId,
  })

  // Fetch related observations (for context)
  const { data: observations = [] } = useQuery({
    queryKey: ['softwareiq', 'observations', missionId],
    queryFn: () => api.softwareiq.observations.list({ missionId }),
    enabled: !!missionId,
  })

  // Filter observations linked to this finding (same CWE, file, line)
  const relatedObservations = observations.filter(
    (obs) =>
      finding &&
      obs.cwe_id === finding.cwe_id &&
      obs.file_path === finding.file_path &&
      obs.line_number === finding.line_number
  )

  // Handle VEX export
  const handleExportVex = async () => {
    if (!missionId) return
    try {
      const vexDoc = await api.softwareiq.vex.exportCsaf(missionId)
      const blob = new Blob([JSON.stringify(vexDoc, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `vex-${findingId?.slice(0, 8)}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (exportError) {
      console.error('VEX export failed:', exportError)
    }
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" asChild>
          <Link to={`/missions/${missionId}`}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Mission
          </Link>
        </Button>
        <Card className="border-destructive/50">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-medium">Error Loading Finding</h3>
            <p className="text-muted-foreground mt-2">
              {error instanceof Error ? error.message : 'Failed to load finding details'}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const severityDisplay = finding ? getSeverityDisplay(finding.jfac_risk.label) : null

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" asChild>
          <Link to={missionId ? `/missions/${missionId}` : '/findings'}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to {missionId ? 'Mission' : 'Findings'}
          </Link>
        </Button>
        {finding && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportVex}>
              <Download className="h-4 w-4 mr-2" />
              Export VEX
            </Button>
          </div>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <div className="grid grid-cols-2 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      )}

      {/* Finding details */}
      {finding && severityDisplay && (
        <>
          {/* Header card */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className={cn('p-3 rounded-lg', severityDisplay.bgColor)}>
                      <Shield className={cn('h-6 w-6', severityDisplay.color)} />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-mono">
                        {finding.cwe_id}
                      </CardTitle>
                      <CardDescription>
                        Finding ID: {finding.finding_id.slice(0, 8)}...
                      </CardDescription>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={severityDisplay.variant} className="text-lg px-4 py-1">
                    {finding.jfac_risk.label}
                  </Badge>
                  <Badge variant="outline" className="text-lg px-4 py-1 font-mono">
                    {finding.jfac_risk.score.toFixed(1)}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* File location */}
              {finding.file_path && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 mb-6">
                  <FileCode className="h-5 w-5 text-muted-foreground" />
                  <code className="font-mono text-sm">
                    {finding.file_path}
                    {finding.line_number && (
                      <span className="text-primary">:{finding.line_number}</span>
                    )}
                  </code>
                </div>
              )}

              {/* Metadata grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <MetadataItem
                  label="Scanner"
                  value={finding.scanner_identity}
                  icon={Crosshair}
                />
                <MetadataItem
                  label="Mission ID"
                  value={finding.mission_id.slice(0, 8) + '...'}
                  icon={Crosshair}
                  mono
                />
                <MetadataItem
                  label="Created"
                  value={new Date(finding.created_at).toLocaleString()}
                  icon={Clock}
                />
                <MetadataItem
                  label="JFAC Score"
                  value={finding.jfac_risk.score.toFixed(2)}
                  icon={Shield}
                  mono
                />
              </div>
            </CardContent>
          </Card>

          {/* Risk Assessment */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-primary" />
                JFAC Risk Assessment
              </CardTitle>
              <CardDescription>
                Calculated using the JFAC 2019 Software Assurance methodology
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Score visualization */}
                <div className="col-span-1 flex flex-col items-center justify-center p-6 rounded-lg bg-muted/30">
                  <div
                    className={cn(
                      'text-6xl font-bold tabular-nums',
                      severityDisplay.color
                    )}
                  >
                    {finding.jfac_risk.score.toFixed(1)}
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">Risk Score</div>
                  <Badge variant={severityDisplay.variant} className="mt-3">
                    {finding.jfac_risk.label}
                  </Badge>
                </div>

                {/* Score breakdown */}
                <div className="col-span-2 space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <Info className="h-5 w-5 text-muted-foreground" />
                      <span>CWE Identifier</span>
                    </div>
                    <code className="font-mono text-primary">{finding.cwe_id}</code>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <User className="h-5 w-5 text-muted-foreground" />
                      <span>Scanner Identity</span>
                    </div>
                    <span className="text-sm">{finding.scanner_identity}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                      <span>Discovery Date</span>
                    </div>
                    <span className="text-sm">
                      {new Date(finding.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Related Observations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                Related Observations
              </CardTitle>
              <CardDescription>
                Scanner observations that contributed to this finding
              </CardDescription>
            </CardHeader>
            <CardContent>
              {relatedObservations.length > 0 ? (
                <div className="space-y-4">
                  {relatedObservations.map((obs) => (
                    <div
                      key={obs.observation_id}
                      className="flex items-center justify-between p-4 rounded-lg border"
                    >
                      <div className="flex items-center gap-4">
                        <Badge variant="default">{obs.status}</Badge>
                        <div>
                          <p className="font-medium">{obs.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {obs.scanner_name} • {obs.observation_id.slice(0, 8)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {obs.vetted_by && (
                          <>
                            <User className="h-4 w-4" />
                            <span>{obs.vetted_by}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No related observations found</p>
                  <p className="text-xs mt-1">
                    Observations are linked by CWE, file path, and line number
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* VEX Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="h-5 w-5 text-primary" />
                VEX Document Status
              </CardTitle>
              <CardDescription>
                Vulnerability Exploitability eXchange (CSAF 2.0)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-6 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="font-medium text-green-500">Included in VEX</p>
                    <p className="text-sm text-muted-foreground">
                      This finding will be included in the mission's VEX export
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleExportVex}>
                  <Download className="h-4 w-4 mr-2" />
                  Export VEX
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
